import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Send, Instagram, Facebook, Linkedin } from "lucide-react";
import PageHero from "@/components/PageHero";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CONTACT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = [
  "Hydroponics Consultancy",
  "Polyhouse Construction",
  "Farm Management",
  "Training",
  "Other",
];

const initialForm = {
  full_name: "",
  phone: "",
  email: "",
  location: "",
  inquiry_type: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.inquiry_type || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Our team will reach out to schedule your site visit.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-border py-3 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary transition-colors duration-300";

  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Contact Us"
        lines={["Let's Grow", "Something Great", "Together"]}
        subtitle="Have a plot of land, a farming idea, or a struggling crop? Our team is ready to help — from the first consultation to the final harvest."
        testId="contact-hero"
      />

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Info */}
          <div className="md:col-span-4">
            <p className="overline text-primary">Get in Touch</p>
            <div className="mt-8 space-y-8">
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email"
                className="flex items-start gap-4 group"
              >
                <Mail className="w-5 h-5 text-primary mt-1 shrink-0" />
                <div>
                  <div className="overline text-muted-foreground">Email</div>
                  <div className="mt-1 group-hover:text-primary transition-colors break-all">
                    {CONTACT.email}
                  </div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-primary mt-1 shrink-0" />
                <div>
                  <div className="overline text-muted-foreground">Location</div>
                  <div className="mt-1">{CONTACT.location}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="w-5 h-5 text-primary mt-1 shrink-0" />
                <div>
                  <div className="overline text-muted-foreground">Phone / WhatsApp</div>
                  <div className="mt-1">{CONTACT.phone}</div>
                </div>
              </div>
            </div>
            <div className="mt-10 flex gap-3">
              {[Instagram, Facebook, Linkedin].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="grid place-items-center w-10 h-10 border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors duration-300"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="md:col-span-7 md:col-start-6">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className="overline text-muted-foreground">Full Name *</label>
                  <input
                    data-testid="contact-input-name"
                    className={inputCls}
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    placeholder="Chandan kumar"
                  />
                </div>
                <div>
                  <label className="overline text-muted-foreground">Phone Number *</label>
                  <input
                    data-testid="contact-input-phone"
                    className={inputCls}
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+91 8804753106"
                  />
                </div>
                <div>
                  <label className="overline text-muted-foreground">Email *</label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="farmingwave@gmail.com"
                  />
                </div>
                <div>
                  <label className="overline text-muted-foreground">Location / City</label>
                  <input
                    data-testid="contact-input-location"
                    className={inputCls}
                    value={form.location}
                    onChange={(e) => update("location", e.target.value)}
                    placeholder="City, State"
                  />
                </div>
              </div>

              <div>
                <label className="overline text-muted-foreground">Type of Inquiry *</label>
                <Select
                  value={form.inquiry_type}
                  onValueChange={(v) => update("inquiry_type", v)}
                >
                  <SelectTrigger
                    data-testid="contact-select-inquiry"
                    className="w-full bg-transparent border-0 border-b border-border rounded-none px-0 py-3 h-auto focus:ring-0 focus:border-primary data-[placeholder]:text-muted-foreground/60"
                  >
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-border">
                    {INQUIRY_TYPES.map((t) => (
                      <SelectItem
                        key={t}
                        value={t}
                        data-testid={`inquiry-option-${t.toLowerCase().replace(/\s+/g, "-")}`}
                        className="rounded-none"
                      >
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="overline text-muted-foreground">Message / Project Details *</label>
                <textarea
                  data-testid="contact-input-message"
                  rows={4}
                  className={`${inputCls} resize-none`}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Tell us about your land, crop, or idea..."
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="group overline inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 font-bold transition-transform duration-300 hover:scale-[1.04] disabled:opacity-60 disabled:hover:scale-100"
              >
                {submitting ? "Sending..." : "Schedule a Free Site Visit"}
                <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
